import React, { useContext, useState } from 'react';
import { UserContext } from '../../../components/Context/userContext';
import { headerLink } from '../../../navigation/function';
import { staffNavigation } from '../../../navigation/staff';
import { Button, Grid, InputAdornment, TextField, Typography, Pagination, Stack } from '@mui/material';
import { Search } from '@mui/icons-material';
import { ShowNotification } from '../../../utils/notification/snackbarUtils';
import { RequestNextApi } from '../../../api/Request';
import { getCookie, setCookie } from '../../../utils/cookieHandle';
import { useRouter } from 'next/router';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { ProgressContext } from '../../../components/Context/progressContext';
import MarketingNavigation from '../../../navigation/marketing';
import Layout from '../../../components/Layout';
import AdvancedSearch from '../../../components/Staff/AdvanceSearch';
import { useAdvancedSearchStore } from '../../../components/Staff/hooks/useAdvancedSearch';
import useDebouncedCallback from '../../../components/hooks/useDebouncedCallback';
import CreateNewAccountButton from '../../../components/Staff/CreateNewAccountButton';

export default function StaffUser() {
    const router = useRouter();
    const { state: userContext, update: updateUser } = useContext(UserContext);
    const [keyword, setKeyword] = useState('');
    const [users, setUsers] = useState([]);
    const [pages, setPages] = useState(null);
    const [resultsCount, setResultsCount] = useState(0);
    const { update: updateProgress } = useContext(ProgressContext);
    const setShowAdvanceFilter = useAdvancedSearchStore(s => s.setShowAdvanceFilter)
    const searchMode = useAdvancedSearchStore(s => s.searchMode)
    const setSearchMode = useAdvancedSearchStore(s => s.setSearchMode)
    const setHasSearchedAdvanced = useAdvancedSearchStore(s => s.setHasSearchedAdvanced)
    const clearAll = useAdvancedSearchStore(s => s.clearAll)

    const onClickSearch = async () => {
        updateProgress({ isLoading: true });
        const res = await memberSearchStaff(keyword, 1, 10);
        if (res.success) {
            setUsers(res.results);
            setPages(res?.pagination?.pages);
            setResultsCount(res?.pagination?.rows);
        } else {
            ShowNotification.error('Member search failed');
        }
        updateProgress({ isLoading: false });
    };

    const handleSearchKeyword = useDebouncedCallback(() => {
        onClickSearch();
    }, 1000);

    const memberSearchStaff = async (keywords, page, limit) => {
        try {
            setSearchMode("REGULAR")
            setHasSearchedAdvanced(false)
            clearAll?.()
            const res = await RequestNextApi.post('/user/member-search-staff', {
                keywords,
                page,
                limit,
                jwtToken: getCookie('jwt-token'),
            });
            if (res.data && res.data.success) {
                return res.data;
            } else {
                ShowNotification.error('Member search failed');
            }
        } catch (error) {
            ShowNotification.error('Member search failed');
        }
    };

    const advancedMemberSearchStaff = async (payload, page, limit) => {
        try {
            setSearchMode("ADVANCED")
            const res = await RequestNextApi.post('/user/member-advanced-search-staff', {
                ...payload,
                page,
                limit,
                jwtToken: getCookie('jwt-token'),
            });
            if (res.data && res.data.success) {
                return res.data;
            } else {
                ShowNotification.error('Advanced member search failed');
            }
        } catch (error) {
            ShowNotification.error('Advanced member search failed');
        }
    };

    const onSubmitAdvancedSearchFilter = async (payload) => {
        try {
            updateProgress({ isLoading: true });
            const res = await advancedMemberSearchStaff(payload, 1, 10);
            if (res.success) {
                setUsers(res.users.map(i => ({
                    id: i.cfpn_id,
                    firmName: i.FirmName,
                    firstName: i.FirstName || "",
                    email: i.Email,
                    lastName: i.LastName,
                    shipCity: i.ShipCity
                })));
                setPages(res?.pagination?.pages);
                setResultsCount(res?.pagination?.rows);
                return true
            } else {
                ShowNotification.error('Advanced member search failed');
                return false
            }
        } catch (e) {
            return false
        } finally {
            updateProgress({ isLoading: false });
        }
    }

    const impersonate = async (event, userId) => {
        event.preventDefault();
        updateProgress({ isLoading: true });

        const res = await RequestNextApi.post('/login/impersonate', {
            impersonating_id: userId,
            jwtToken: getCookie('jwt-token'),
        });
        if (res.data && res.data.success) {
            setCookie('jwt-token', res.data.impersonating_token.jwtToken);
            setCookie('jwt-refresh-token', res.data.impersonating_token.refreshToken, { expires: 3 });
            const userInfo = await RequestNextApi.post('/user/user-fetch2', {
                jwtToken: res.data.impersonating_token.jwtToken,
            });
            if (userInfo.data.success) {
                updateUser({ user: userInfo.data.user });
                router.push('/members/home');
            } else {
                ShowNotification.error('Login failed');
            }
        } else {
            ShowNotification.error('Login failed');
        }
        updateProgress({ isLoading: false });
    };
    const handlePageChange = async (event, value) => {
        updateProgress({ isLoading: true });
        const res = searchMode === "REGULAR" ?
            await memberSearchStaff(keyword, value, 10) :
            await advancedMemberSearchStaff(useAdvancedSearchStore.getState().getPayloadForApi(), value, 10);
        if (res.success) {
            setUsers(searchMode === "REGULAR" ? res.results : res.users.map(i => ({
                id: i.cfpn_id,
                firmName: i.FirmName,
                firstName: i.FirstName || "",
                email: i.Email,
                lastName: i.LastName,
                shipCity: i.ShipCity
            })));
            setPages(res?.pagination?.pages);
            setResultsCount(res?.pagination?.rows);
        } else {
            ShowNotification.error('Member search failed');
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        updateProgress({ isLoading: false });
    };

    const onClickAdvanceSearch = () => {
        setShowAdvanceFilter(true)
    }

    return (
        <MarketingNavigation isAdmin>
            <Layout>
                {userContext?.user?.isStaff ? (
                    <div>
                        <h1 className="text-xl text-primary-500 mt-8">
                            Customer Relationship Manager (CRM)
                        </h1>
                        <div className="bg-white p-4 mt-8 rounded-2xl">
                            <Typography component="div" className="text-secondary-500 text-xl font-bold">
                                Search the Database
                            </Typography>
                            <Grid container spacing={2} className="items-center">
                                <Grid item xs={12} md={6} xl={3}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        id="keyword"
                                        name="keyword"
                                        placeholder="ID, First Name, Last Name etc."
                                        className="my-3"
                                        onChange={(e) => {
                                            setKeyword(e.target.value)
                                            handleSearchKeyword()
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6} xl={3}>
                                    <div className="flex justify-start 3xl:justify-end">
                                        <Button variant="contained" className="bg-secondary-500 hover:bg-secondary-500" onClick={onClickAdvanceSearch}>
                                            Advanced Search
                                        </Button>
                                        <div className="ml-4">
                                            <CreateNewAccountButton />
                                        </div>
                                        <Button variant="contained" className="bg-secondary-500 hover:bg-secondary-500 ml-4" onClick={() => router.push('/staff/user/duplicate-accounts')}>
                                            Merge Accounts
                                        </Button>
                                    </div>
                                </Grid>
                            </Grid>
                            <TableContainer>
                                <Table stickyHeader aria-label="sticky table" className="z-2">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align={'left'} style={{ minWidth: 150 }}>
                                                Id
                                            </TableCell>
                                            <TableCell align={'left'} style={{ minWidth: 150 }}>
                                                First Name
                                            </TableCell>
                                            <TableCell align={'left'} style={{ minWidth: 150 }} className="bg-white">
                                                Last Name
                                            </TableCell>
                                            <TableCell align={'left'} style={{ minWidth: 150 }}>
                                                Firm Name
                                            </TableCell>
                                            <TableCell align={'left'} style={{ minWidth: 150 }}>
                                                Email
                                            </TableCell>
                                            <TableCell align={'left'} style={{ minWidth: 150 }}>
                                                Ship City
                                            </TableCell>
                                            <TableCell align={'left'} style={{ minWidth: 150 }}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.length === 0 && (
                                            <TableRow className="bg-white">
                                                <TableCell align={'center'} colSpan={7}>
                                                    No Results
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {users.map((row) => {
                                            return (
                                                <TableRow hover role="checkbox" tabIndex={-1} key={row.id} className="bg-white">
                                                    <TableCell align={'left'}>{row.id}</TableCell>
                                                    <TableCell align={'left'}>{row.firstName}</TableCell>
                                                    <TableCell align={'left'}>{row.lastName}</TableCell>
                                                    <TableCell align={'left'}>{row.firmName}</TableCell>
                                                    <TableCell align={'left'}>{row.email}</TableCell>
                                                    <TableCell align={'left'}>{row.shipCity}</TableCell>
                                                    <TableCell align={'left'}>
                                                        <Stack direction="row">
                                                            <Button
                                                                type="submit"
                                                                variant="contained"
                                                                color="secondary"
                                                                className="mr-2"
                                                                onClick={(event) => {
                                                                    router.push(`/staff/user/${row.id}`);
                                                                }}
                                                            >
                                                                CRM
                                                            </Button>
                                                            <Button
                                                                type="submit"
                                                                variant="contained"
                                                                color="secondary"
                                                                onClick={(event) => {
                                                                    impersonate(event, row.id);
                                                                }}
                                                            >
                                                                Impersonate
                                                            </Button>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <AdvancedSearch
                                onSubmit={onSubmitAdvancedSearchFilter}
                                resultsCount={resultsCount}
                                onClear={() => {
                                    clearAll?.()
                                    setSearchMode("REGULAR")
                                    setUsers([])
                                    setPages(null)
                                }}
                            />
                        </div>
                        {users.length !== 0 && (
                            <div className="mt-5 mb-[100px] flex justify-end">
                                <Pagination count={pages} onChange={handlePageChange} />
                            </div>
                        )}
                    </div>
                ) : (
                    <></>
                )}

            </Layout>
        </MarketingNavigation>
    );
}

StaffUser.getLayout = (page) => <>{page}</>;
StaffUser.isStaff = true;
